/**
 * CC-14: the one place money and dates get formatted. Every future screen
 * showing a price, a scheduled time, or an invoice date must go through
 * these, not `` `$${amount}` `` or `date.toLocaleDateString()` inline — the
 * locale argument is what makes "$1,234.56" vs "1.234,56 €" a config change
 * later instead of a find-and-replace across the app.
 *
 * Defaults to the browser's own locale (`navigator.language`), not a
 * hardcoded `'en-US'` — money and dates should already read correctly for
 * whoever is looking at the screen, even before a second UI language ships.
 */

export function formatCurrency(
  amount: number,
  currency = 'USD',
  locale: string = navigator.language,
): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
}

export function formatDate(
  date: Date | string,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
  locale: string = navigator.language,
): string {
  const value = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale, options).format(value);
}

export function formatDateTime(date: Date | string, locale: string = navigator.language): string {
  return formatDate(date, { dateStyle: 'medium', timeStyle: 'short' }, locale);
}

/**
 * A time of day, read on a named clock rather than the viewer's (M1-09).
 *
 * The dispatch board prints the time a technician will actually knock on the
 * door, which is a time on the *business's* clock. Everywhere else in the app
 * the browser's own zone is the right answer and `timeZone` can be omitted;
 * here it is the whole point, so it is a parameter rather than a default.
 */
export function formatTime(
  date: Date | string,
  timeZone?: string,
  locale: string = navigator.language,
): string {
  return formatDate(date, { timeStyle: 'short', timeZone }, locale);
}

/** A weekday and date, on a named clock — the dispatch board's day heading. */
export function formatBoardDate(
  date: Date | string,
  timeZone?: string,
  locale: string = navigator.language,
): string {
  return formatDate(
    date,
    { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone },
    locale,
  );
}
